import { Component, input, output, signal, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface DragDropItem {
  id: string;
  data: any;
}

@Component({
  selector: 'app-drag-drop',
  imports: [CommonModule],
  template: `
    <div 
      #container
      [class]="containerClasses"
      [attr.aria-label]="'Drag and drop container'">
      @for (item of items(); track item.id) {
        <div
          [class]="getItemClasses(item)"
          [attr.draggable]="!disabled()"
          (dragstart)="onDragStart($event, item)"
          (dragend)="onDragEnd()"
          (dragover)="onDragOver($event)"
          (drop)="onDrop($event, item)"
          (dragleave)="onDragLeave()">
          <ng-container *ngTemplateOutlet="itemTemplate; context: { $implicit: item }"></ng-container>
        </div>
      }
    </div>
    
    <ng-template #itemTemplate let-item>
      <ng-content></ng-content>
    </ng-template>
  `,
  styles: []
})
export class DragDropComponent implements AfterViewInit {
  items = input<DragDropItem[]>([]);
  disabled = input<boolean>(false);
  orientation = input<'vertical' | 'horizontal'>('vertical');

  dragStartEvent = output<DragDropItem>();
  dropEvent = output<{ item: DragDropItem; target: DragDropItem }>();
  reorderEvent = output<DragDropItem[]>();

  @ViewChild('container', { static: false }) container!: ElementRef<HTMLElement>;

  draggedItem = signal<DragDropItem | null>(null);
  dragOverIndex = signal<number>(-1);

  ngAfterViewInit(): void {
    // Component initialized
  }

  onDragStart(event: DragEvent, item: DragDropItem): void {
    if (this.disabled()) {
      event.preventDefault();
      return;
    }
    this.draggedItem.set(item);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', item.id);
    }
    this.dragStartEvent.emit(item);
  }

  onDragEnd(): void {
    this.draggedItem.set(null);
    this.dragOverIndex.set(-1);
  }

  onDragOver(event: DragEvent): void {
    if (this.disabled() || !this.draggedItem()) return;
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onDrop(event: DragEvent, targetItem: DragDropItem): void {
    event.preventDefault();
    const dragged = this.draggedItem();
    if (!dragged || dragged.id === targetItem.id) {
      this.onDragEnd();
      return;
    }

    const items = [...this.items()];
    const draggedIndex = items.findIndex(i => i.id === dragged.id);
    const targetIndex = items.findIndex(i => i.id === targetItem.id);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      items.splice(draggedIndex, 1);
      items.splice(targetIndex, 0, dragged);
      this.reorderEvent.emit(items);
      this.dropEvent.emit({ item: dragged, target: targetItem });
    }

    this.onDragEnd();
  }

  onDragLeave(): void {
    this.dragOverIndex.set(-1);
  }

  getItemClasses(item: DragDropItem): string {
    const classes = [
      'transition-all',
      'duration-200',
      'cursor-move',
      'rounded-md',
      'p-2'
    ];

    if (this.draggedItem()?.id === item.id) {
      classes.push('opacity-50', 'scale-95');
    }

    if (this.dragOverIndex() >= 0) {
      classes.push('border-2', 'border-blue-500', 'border-dashed');
    } else {
      classes.push('border', 'border-transparent', 'hover:border-gray-300', 'dark:hover:border-gray-600');
    }

    if (this.disabled()) {
      classes.push('opacity-50', 'cursor-not-allowed');
    }

    return classes.join(' ');
  }

  get containerClasses(): string {
    const classes = ['w-full'];
    if (this.orientation() === 'horizontal') {
      classes.push('flex', 'flex-row', 'gap-2');
    } else {
      classes.push('flex', 'flex-col', 'gap-2');
    }
    return classes.join(' ');
  }
}

